import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  CreditCard,
  Check,
  ArrowLeft,
  Lock,
  ShieldCheck,
  Copy,
  Smartphone,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, LanguageToggle } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CASHFREE_JS_SRC = 'https://sdk.cashfree.com/js/v3/cashfree.js';

const loadCashfreeSdk = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('No window'));
    if (window.Cashfree) return resolve(window.Cashfree);
    const existing = document.querySelector(`script[src="${CASHFREE_JS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Cashfree));
      existing.addEventListener('error', () => reject(new Error('SDK failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = CASHFREE_JS_SRC;
    s.async = true;
    s.onload = () => resolve(window.Cashfree);
    s.onerror = () => reject(new Error('SDK failed'));
    document.head.appendChild(s);
  });

const PaymentGateway = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [info, setInfo] = useState({
    upi_id: 'divineheal7-1@okaxis',
    business_name: 'Divine Quanta',
    business_whatsapp: '8920443069',
    cashfree_enabled: false,
    paypal_me_id: '',
    paypal_enabled: false,
  });
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [paymentAttempted, setPaymentAttempted] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/payments/packages`)
      .then((r) => r.json())
      .then(setPackages)
      .catch(() => toast.error('Failed to load packages'));
    fetch(`${BACKEND_URL}/api/payments/info`)
      .then((r) => r.json())
      .then(setInfo)
      .catch(() => {});
  }, []);

  // Fetch QR whenever package or currency changes
  useEffect(() => {
    if (!selectedPkg || currency !== 'INR') {
      setQrData(null);
      return;
    }
    setQrLoading(true);
    fetch(`${BACKEND_URL}/api/payments/upi-qr?package_id=${selectedPkg}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setQrData(d))
      .catch(() => setQrData(null))
      .finally(() => setQrLoading(false));
  }, [selectedPkg, currency]);

  const selectedPackage = packages.find((p) => p.id === selectedPkg);

  const displayAmount = (pkg) =>
    currency === 'USD'
      ? `$${pkg.amount_usd}`
      : `₹${pkg.amount_inr.toLocaleString('en-IN')}`;

  const inrAmount = selectedPackage?.amount_inr ?? 0;

  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText(info.upi_id);
      toast.success('UPI ID copied');
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  const openUpiApp = () => {
    if (!selectedPackage) {
      toast.error('Please select a package first');
      return;
    }
    const link = `upi://pay?pa=${encodeURIComponent(info.upi_id)}&pn=${encodeURIComponent(
      info.business_name
    )}&am=${inrAmount}&cu=INR&tn=${encodeURIComponent(selectedPackage.name)}`;
    window.location.href = link;
    setPaymentAttempted(true);
    toast.success('Opening your UPI app…');
  };

  const openPaypal = () => {
    if (!selectedPackage) {
      toast.error('Please select a package first');
      return;
    }
    if (!info.paypal_me_id) {
      toast.info('PayPal is being configured. Please try again shortly or use UPI above.');
      return;
    }
    const usd = selectedPackage.amount_usd;
    const url = `https://www.paypal.com/paypalme/${encodeURIComponent(info.paypal_me_id)}/${usd}USD`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setPaymentAttempted(true);
    toast.success('Opening PayPal…');
  };

  const sendPaymentConfirmation = () => {
    if (!selectedPackage) return;
    const msg = encodeURIComponent(
      `Hi Divine Quanta team! 🙏\n\nI have just paid ₹${inrAmount} for "${selectedPackage.name}" via UPI to ${info.upi_id}.\n\nPlease confirm receipt and share my report on this WhatsApp.\n\nThank you!`
    );
    window.open(
      `https://wa.me/91${info.business_whatsapp}?text=${msg}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const sendPaypalConfirmation = () => {
    if (!selectedPackage) return;
    const msg = encodeURIComponent(
      `Hi Divine Quanta team! 🙏\n\nI have just paid $${selectedPackage.amount_usd} USD for "${selectedPackage.name}" via PayPal (paypal.me/${info.paypal_me_id}).\n\nPlease confirm receipt and share my report on this WhatsApp.\n\nThank you!`
    );
    window.open(
      `https://wa.me/91${info.business_whatsapp}?text=${msg}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const validateForCashfree = () => {
    if (!selectedPkg) {
      toast.error('Please select a package');
      return false;
    }
    if (!name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!phone.trim() || !/^\d{10,15}$/.test(phone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid phone number (10–15 digits)');
      return false;
    }
    return true;
  };

  const handleCashfreePayment = async () => {
    if (!info.cashfree_enabled) {
      toast.info('Cashfree checkout will be available shortly. Please use UPI for now.');
      return;
    }
    if (!validateForCashfree()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/cashfree/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: selectedPkg,
          origin_url: window.location.origin,
          customer_name: name.trim(),
          customer_email: email.trim(),
          customer_phone: phone.replace(/\D/g, ''),
          currency,
        }),
      });
      if (!res.ok) {
        toast.error('Could not start checkout. Please try UPI for now.');
        return;
      }
      const data = await res.json();
      const CashfreeFactory = await loadCashfreeSdk();
      const cashfree = CashfreeFactory({
        mode: (data.environment || 'TEST').toUpperCase() === 'PROD' ? 'production' : 'sandbox',
      });
      await cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: '_self',
      });
    } catch {
      toast.error('Could not start checkout.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-950/90 via-indigo-950/80 to-black/90" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-10">
          <button
            onClick={() => navigate('/home')}
            className="text-purple-300 hover:text-white transition-all"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1
              className="text-3xl font-bold text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('Secure Payment', 'सुरक्षित भुगतान')}
            </h1>
            <p className="text-purple-300 text-sm">
              {t(<>UPI for India (GPay, PhonePe, BHIM) &middot; PayPal for international clients</>, 'भारत के लिए UPI (GPay, PhonePe, BHIM) · अंतरराष्ट्रीय क्लाइंट्स के लिए PayPal')}
            </p>
          </div>
          <LanguageToggle />
        </div>

        {/* Thank You Note from Founder */}
        <div className="mb-8" data-testid="pay-founder-note">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 via-amber-500/8 to-orange-500/10 blur-2xl"></div>
            <div className="relative bg-gradient-to-br from-purple-900/50 via-indigo-950/60 to-purple-900/50 backdrop-blur-xl border border-yellow-400/30 rounded-2xl p-5 flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400/40 to-amber-500/40 border border-yellow-400/60 flex items-center justify-center">
                <span className="text-yellow-200 font-bold text-sm" style={{fontFamily: 'Cormorant Garamond, serif'}}>DQ</span>
              </div>
              <div className="flex-1">
                <p className="text-white text-sm md:text-base leading-relaxed" style={{fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic'}}>
                  &ldquo;<span className="text-yellow-100">{t('Thank you for showing your trust in Divine Quanta.', 'डिवाइन क्वांटा पर विश्वास दिखाने के लिए धन्यवाद।')}</span> {t('We are truly happy to serve you in your healing journey.', 'आपकी उपचार यात्रा में सेवा का अवसर पाकर हम अत्यंत प्रसन्न हैं।')}&rdquo;
                  <span className="not-italic text-yellow-300 font-semibold ml-2 whitespace-nowrap">&mdash; {t('Divine Quanta Team', 'डिवाइन क्वांटा टीम')}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Package */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-purple-200 mb-4">1. {t('Select Your Package', 'अपना पैकेज चुनें')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setSelectedPkg(pkg.id)}
                className={`relative text-left p-5 rounded-2xl border-2 transition-all backdrop-blur-sm ${
                  selectedPkg === pkg.id
                    ? 'border-cyan-400/70 bg-cyan-500/15 scale-[1.02]'
                    : 'border-purple-400/20 bg-purple-900/20 hover:border-purple-400/40'
                }`}
                data-testid={`pkg-${pkg.id}`}
              >
                {selectedPkg === pkg.id && (
                  <div className="absolute -top-2 -right-2 bg-cyan-500 rounded-full p-1">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <p className="text-white font-bold text-sm mb-2">{pkg.name}</p>
                <span className="text-cyan-300 text-xl font-bold">{displayAmount(pkg)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Payment */}
        {selectedPkg && (
          <div className="space-y-6">
            {/* UPI Section — Active */}
            {(true) && (
              <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 backdrop-blur-xl border-2 border-green-400/40 rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="bg-green-500/20 p-3 rounded-xl border border-green-400/30">
                    <Smartphone className="w-6 h-6 text-green-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">{t('Pay via UPI', 'UPI से भुगतान करें')}</h3>
                    <p className="text-green-200 text-sm">
                      {t('GPay, PhonePe, BHIM — any UPI app works', 'GPay, PhonePe, BHIM — कोई भी UPI ऐप चलेगा')}
                    </p>
                  </div>
                  <span className="text-green-300 text-xs font-bold bg-green-500/20 px-3 py-1 rounded-full">
                    ACTIVE
                  </span>
                </div>

                <div className="bg-black/30 border border-green-400/20 rounded-2xl p-5 mb-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-green-300 text-xs uppercase tracking-wide mb-1">UPI ID</p>
                      <p className="text-white font-mono text-lg">{info.upi_id}</p>
                    </div>
                    <Button
                      onClick={copyUpiId}
                      variant="ghost"
                      className="text-green-300 hover:text-white hover:bg-green-500/20"
                      data-testid="copy-upi-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {t('Copy', 'कॉपी करें')}
                    </Button>
                  </div>
                  {selectedPackage && (
                    <div className="mt-4 pt-4 border-t border-green-400/20 flex items-center justify-between">
                      <div>
                        <p className="text-green-300 text-xs uppercase tracking-wide mb-1">
                          {t('Amount to pay', 'देय राशि')}
                        </p>
                        <p className="text-white text-2xl font-bold">
                          ₹{inrAmount.toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-300 text-xs uppercase tracking-wide mb-1">
                          {t('For', 'के लिए')}
                        </p>
                        <p className="text-white text-sm">{selectedPackage.name}</p>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={openUpiApp}
                  disabled={!selectedPackage}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 rounded-full text-lg font-bold"
                  style={{ boxShadow: '0 0 30px rgba(34,197,94,0.3)' }}
                  data-testid="pay-upi-btn"
                >
                  <Smartphone className="w-5 h-5 mr-2" />
                  {t('Open UPI App to Pay', 'UPI ऐप खोलकर भुगतान करें')} ₹{inrAmount.toLocaleString('en-IN')}
                </Button>

                {/* QR Code — scan with any UPI app */}
                {qrData?.qr_image_base64 && (
                  <div className="mt-6 bg-white rounded-2xl p-5 flex flex-col items-center" data-testid="upi-qr-block">
                    <img
                      src={qrData.qr_image_base64}
                      alt="UPI QR Code"
                      className="w-48 h-48 rounded-lg"
                      data-testid="upi-qr-image"
                    />
                    <p className="text-gray-700 text-sm font-semibold mt-3">
                      {t('Scan with any UPI app', 'किसी भी UPI ऐप से स्कैन करें')}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {t('Amount auto-fills as', 'राशि अपने-आप भर जाएगी:')} ₹{inrAmount.toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
                {qrLoading && (
                  <div className="mt-6 bg-white/10 rounded-2xl p-5 flex justify-center items-center h-48">
                    <p className="text-purple-200 text-sm">Generating QR code…</p>
                  </div>
                )}

                {/* Safety note */}
                <p className="text-amber-200/80 text-xs mt-4 text-center bg-amber-500/10 border border-amber-400/20 rounded-xl p-3" data-testid="upi-safety-note">
                  ⚠️ {t('Verify payee name shows', 'भुगतान से पहले जाँच लें कि प्राप्तकर्ता का नाम')} <strong>&quot;{info.business_name}&quot;</strong> {t('in your UPI app before confirming payment.', 'आपके UPI ऐप में दिख रहा हो।')}
                </p>

                {/* After Payment — Send Confirmation */}
                <div className="mt-5 bg-black/40 border border-green-400/30 rounded-2xl p-5">
                  <p className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" /> {t('After Paying', 'भुगतान के बाद')}
                  </p>
                  <p className="text-green-200/80 text-xs mb-4">
                    {t(<>Tap below to notify Divine Quanta on WhatsApp. Once payment is verified, you&apos;ll receive a confirmation message and your report on +91&nbsp;{info.business_whatsapp}.</>,
                      <>नीचे टैप कर डिवाइन क्वांटा को व्हाट्सएप पर सूचित करें। भुगतान सत्यापित होते ही आपको +91&nbsp;{info.business_whatsapp} पर पुष्टि संदेश और आपकी रिपोर्ट मिलेगी।</>)}
                  </p>
                  <Button
                    onClick={sendPaymentConfirmation}
                    disabled={!selectedPackage}
                    className={`w-full ${
                      paymentAttempted
                        ? 'bg-green-700 hover:bg-green-800'
                        : 'bg-gradient-to-r from-emerald-700 to-green-700 hover:from-emerald-800 hover:to-green-800'
                    } text-white py-4 rounded-full text-sm font-semibold whitespace-normal h-auto`}
                    data-testid="send-payment-confirmation-btn"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    {t('Send Payment Confirmation on WhatsApp', 'व्हाट्सएप पर भुगतान की पुष्टि भेजें')}
                  </Button>
                </div>

                <p className="text-green-200/70 text-xs mt-4 text-center">
                  {t("Mobile: button opens your UPI app directly. Desktop: scan the QR with your phone's UPI app.",
                    'मोबाइल: बटन सीधे आपका UPI ऐप खोलेगा। डेस्कटॉप: QR को फोन के UPI ऐप से स्कैन करें।')}
                </p>
              </div>
            )}

            {/* PayPal Section — for international customers */}
            <div
              className={`bg-gradient-to-br from-blue-900/40 to-indigo-900/40 backdrop-blur-xl border-2 rounded-3xl p-8 ${
                info.paypal_enabled ? 'border-blue-400/50' : 'border-purple-400/20 opacity-90'
              }`}
              data-testid="paypal-section"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="bg-blue-500/25 p-3 rounded-xl border border-blue-400/40">
                  {/* PayPal-style icon */}
                  <svg className="w-6 h-6 text-blue-200" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 4.723-6.156 5.196a12.4 12.4 0 0 1-1.628.106H10.6a.483.483 0 0 0-.477.41l-1.548 9.812a.323.323 0 0 0 .32.375h3.94a.83.83 0 0 0 .82-.703l.034-.176.65-4.126.041-.226a.83.83 0 0 1 .82-.703h.516c3.344 0 5.964-1.36 6.73-5.291.32-1.642.156-3.014-.505-3.977z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">
                    {t(<>PayPal &mdash; International Payment</>, 'PayPal — अंतरराष्ट्रीय भुगतान')}
                  </h3>
                  <p className="text-blue-200 text-sm">
                    {t(<>Cards &amp; PayPal balance from anywhere in the world (USD)</>, 'दुनिया में कहीं से भी कार्ड और PayPal बैलेंस (USD)')}
                  </p>
                </div>
                <span
                  className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${
                    info.paypal_enabled
                      ? 'text-blue-200 bg-blue-500/25'
                      : 'text-amber-300 bg-amber-500/15'
                  }`}
                >
                  {info.paypal_enabled ? 'ACTIVE' : 'COMING SOON'}
                </span>
              </div>

              {info.paypal_enabled ? (
                <>
                  <div className="bg-black/30 border border-blue-400/25 rounded-2xl p-5 mb-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-300 text-xs uppercase tracking-wide mb-1">
                          {t('Amount to pay', 'देय राशि')}
                        </p>
                        <p className="text-white text-2xl font-bold">
                          ${selectedPackage?.amount_usd} USD
                        </p>
                        {selectedPackage && (
                          <p className="text-blue-200 text-xs mt-1">
                            {t('For:', 'के लिए:')} {selectedPackage.name}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-blue-300 text-xs uppercase tracking-wide mb-1">
                          {t('Paying to', 'भुगतान प्राप्तकर्ता')}
                        </p>
                        <p className="text-white text-sm font-mono">
                          paypal.me/{info.paypal_me_id}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={openPaypal}
                    disabled={!selectedPackage}
                    className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white py-6 rounded-full text-lg font-bold"
                    style={{ boxShadow: '0 0 30px rgba(0,112,186,0.35)' }}
                    data-testid="pay-paypal-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                    </svg>
                    {t('Pay', 'भुगतान करें')} ${selectedPackage?.amount_usd} {t('with PayPal', 'PayPal से')}
                  </Button>

                  <p className="text-blue-200/70 text-xs mt-4 text-center">
                    {t(<>You&apos;ll be redirected to PayPal&apos;s secure checkout. Cards accepted without a PayPal account.</>,
                      'आपको PayPal के सुरक्षित चेकआउट पर भेजा जाएगा। बिना PayPal खाते के भी कार्ड स्वीकार्य हैं।')}
                  </p>

                  <div className="bg-green-900/20 border border-green-400/30 rounded-2xl p-4 mt-5">
                    <p className="text-green-200 text-sm mb-3 text-center">
                      {t(<>After paying, notify our team on WhatsApp to receive your confirmation &amp; report.</>,
                        'भुगतान के बाद, पुष्टि और रिपोर्ट पाने के लिए हमारी टीम को व्हाट्सएप पर सूचित करें।')}
                    </p>
                    <Button
                      onClick={sendPaypalConfirmation}
                      disabled={!selectedPackage}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-5 rounded-full font-semibold whitespace-normal h-auto"
                      data-testid="paypal-whatsapp-confirm-btn"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {t('Send Payment Confirmation on WhatsApp', 'व्हाट्सएप पर भुगतान की पुष्टि भेजें')}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="bg-black/30 border border-purple-400/20 rounded-2xl p-5 flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-200 font-semibold mb-1">
                      International payment activating soon
                    </p>
                    <p className="text-purple-200 text-sm">
                      We&apos;re finalising our PayPal setup for international clients. Please
                      use UPI above if you&apos;re in India, or WhatsApp our team at +91 {info.business_whatsapp} for alternate options.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-center justify-center gap-2 text-blue-200/70 text-xs">
                <Lock className="w-3 h-3" />
                <ShieldCheck className="w-3 h-3" />
                <span>Encrypted &amp; buyer-protected via PayPal</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentGateway;
